import FadeinText from '@components/animation/fadein-text';
import Container from '@components/container';
import Vimeo, { vimeoPresets } from '@components/vimeo';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { ContainerSize, VideoCarousel, VideoCarouselItem } from '@lib/types';
import { useEffect, useMemo, useRef, useState } from 'react';

export default function VideoCarouselSection({
  data: { videos },
}: {
  data: VideoCarousel;
}) {
  const [playingVideoIndex, setPlayingVideoIndex] = useState<number>(0);
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [preset, setPreset] = useState<keyof typeof vimeoPresets | null>(null);
  const galleryRef = useRef<HTMLDivElement | null>(null);
  const videoUrls = useMemo(() => {
    return videos.map((video) => video.vimeoUrl);
  }, [videos]);

  useEffect(() => {
    const fetchThumbnails = async () => {
      const thumbnailPromises = videoUrls.map(async (url) => {
        const response = await fetch(
          `https://vimeo.com/api/oembed.json?url=${url}`,
        );
        const data = await response.json();
        return data.thumbnail_url;
      });
      const results = await Promise.all(thumbnailPromises);
      setThumbnails(results);
    };

    fetchThumbnails();
  }, [videoUrls]);

  useEffect(() => {
    const updateScrollState = () => {
      const gallery = galleryRef.current;
      if (gallery) {
        setCanScrollRight(
          gallery.scrollWidth > gallery.clientWidth + gallery.scrollLeft,
        );
        setCanScrollLeft(gallery.scrollLeft > 0);
      }
    };

    updateScrollState();

    const gallery = galleryRef.current;
    gallery?.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);

    return () => {
      gallery?.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [thumbnails]);

  const scrollRight = () => {
    const gallery = galleryRef.current;
    if (gallery) {
      gallery.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const scrollLeft = () => {
    const gallery = galleryRef.current;
    if (gallery) {
      gallery.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const playingVideo: VideoCarouselItem = videos[playingVideoIndex];

  return (
    <Container size={ContainerSize.wide}>
      <div className="lg:grid grid-cols-12 gap-1.5 md:gap-6 items-center">
        <div className="col-start-2 col-span-6 rounded-xl md:rounded-3xl overflow-hidden">
          <Vimeo url={playingVideo.vimeoUrl} preset={preset} />
        </div>
        <div className="mt-4 lg:mt-0 col-start-2 lg:col-start-8 col-span-10 lg:col-span-4">
          <div className="text-blue-800 text-22 leading-130 lg:text-35 lg:leading-130 font-bold">
            <FadeinText text={playingVideo.title} />
          </div>
          {playingVideo.description && (
            <div
              className="prose image-text mt-4"
              dangerouslySetInnerHTML={{ __html: playingVideo.description }}
            />
          )}
        </div>
      </div>
      <div className="lg:grid grid-cols-12 gap-1.5 md:gap-6">
        <div className="col-start-2 col-span-6 mt-8 lg:mt-3 relative">
          <div
            ref={galleryRef}
            className="flex items-start gap-x-2 overflow-x-auto scrollbar-hide">
            {thumbnails.map((imgSrc, index) => {
              const video = videos[index % videos.length];

              return (
                <button
                  key={index}
                  className="shrink-0 w-32 text-left"
                  onClick={() => {
                    setPreset('thumbnail');
                    setPlayingVideoIndex(index);
                  }}>
                  <div className="relative">
                    <img
                      src={imgSrc}
                      alt={video.title}
                      className="rounded cursor-pointer"
                    />
                    {playingVideoIndex === index && (
                      <div className="absolute inset-0 rounded bg-iceblue-500 text-white text-15 font-bold uppercase bg-opacity-90 flex items-center justify-center">
                        Playing
                      </div>
                    )}
                  </div>
                  <div className="text-blue-800 text-17 leading-160 mt-4 line-clamp-2">
                    {video.thumbnailTitle || video.title}
                  </div>
                </button>
              );
            })}
            {canScrollRight && (
              <>
                <div className="absolute right-0 top-0 bg-gradient-to-r from-transparent to-white w-32 h-[72px]"></div>
                <button
                  className="absolute right-0 top-4 bg-white rounded-full w-10 h-10 flex justify-center items-center shadow"
                  onClick={scrollRight}>
                  <ChevronRightIcon className="w-8 h-8" />
                </button>
              </>
            )}
            {canScrollLeft && (
              <>
                <div className="absolute left-0 top-0 bg-gradient-to-l from-transparent to-white w-32 h-[72px]"></div>
                <button
                  className="absolute left-0 top-4 bg-white rounded-full w-10 h-10 flex justify-center items-center shadow"
                  onClick={scrollLeft}>
                  <ChevronLeftIcon className="w-8 h-8" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </Container>
  );
}
